/**
 * Compute the patient's age in whole months at a given measurement date.
 *
 * Inputs are partial-ISO strings (`YYYY`, `YYYY-MM`, or `YYYY-MM-DD`) —
 * missing components fall back to January / day 1 so a year-only DOB
 * still produces a reasonable age. Returns `0` when DOB is unknown or
 * the measurement date predates the DOB; the BE rejects null but accepts
 * 0 as "unknown / not applicable".
 */
export function computeAgeInMonths(
  dateOfBirthIso: string | null,
  measurementDateIso: string,
): number {
  if (dateOfBirthIso === null) return 0;
  const dob = parsePartialIso(dateOfBirthIso);
  const meas = parsePartialIso(measurementDateIso);
  if (dob === null || meas === null) return 0;
  let months =
    (meas.year - dob.year) * 12 + (meas.month - dob.month);
  if (meas.day < dob.day) months -= 1;
  return months < 0 ? 0 : months;
}

function parsePartialIso(
  iso: string,
): { year: number; month: number; day: number } | null {
  const [yearStr, monthStr, dayStr] = iso.split('-');
  const year = Number.parseInt(yearStr ?? '', 10);
  if (!Number.isFinite(year)) return null;
  const month = Number.parseInt(monthStr ?? '', 10);
  const day = Number.parseInt(dayStr ?? '', 10);
  return {
    year,
    month: Number.isFinite(month) ? month : 1,
    day: Number.isFinite(day) ? day : 1,
  };
}
